/**
 * GridSettings.tsx
 * Grid layout specific settings component
 * Path: components/admin-tabs/reviews/display-settings/GridSettings.tsx
 */

import React from 'react';
import { WidgetSettings } from '../../../../types';

interface GridSettingsProps {
  settings: Partial<WidgetSettings>;
  onUpdate: (updates: Partial<WidgetSettings>) => void;
}

export const GridSettings: React.FC<GridSettingsProps> = ({ settings, onUpdate }) => {
  return (
    <div className="space-y-6">
      {/* Grid Columns */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Grid Columns
        </label>
        <select
          value={settings.reviews_per_page || 6}
          onChange={(e) => onUpdate({ reviews_per_page: parseInt(e.target.value) })}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={3}>3 per row</option>
          <option value={4}>4 per row</option>
          <option value={6}>6 per row (2 rows)</option>
          <option value={9}>9 per row (3 rows)</option>
          <option value={12}>12 per row (4 rows)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Number of reviews to display in grid layout</p>
      </div>

      {/* Card Style */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Style
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onUpdate({ card_style: 'bordered' })}
            className={`p-4 border-2 rounded-lg text-sm font-medium transition-all ${
              settings.card_style === 'bordered'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="w-full h-20 border border-gray-300 rounded mb-2"></div>
            Bordered
          </button>
          <button
            onClick={() => onUpdate({ card_style: 'shadow' })}
            className={`p-4 border-2 rounded-lg text-sm font-medium transition-all ${
              settings.card_style === 'shadow'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="w-full h-20 bg-white shadow-md rounded mb-2"></div>
            Shadow
          </button>
        </div>
      </div>

      {/* Gap Between Cards */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Gap Between Cards
        </label>
        <input
          type="range"
          min="8"
          max="32"
          step="4"
          value={settings.card_gap || 16}
          onChange={(e) => onUpdate({ card_gap: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>Tight</span>
          <span className="font-medium">{settings.card_gap || 16}px</span>
          <span>Loose</span>
        </div>
      </div>

      {/* Display Options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Display Options
        </label>
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.show_verified_badge ?? true}
              onChange={(e) => onUpdate({ show_verified_badge: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show verified purchase badge</span>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.show_response ?? true}
              onChange={(e) => onUpdate({ show_response: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show seller responses</span>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.show_media ?? true}
              onChange={(e) => onUpdate({ show_media: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show customer photos/videos</span>
          </label>

          {/* NEW SETTING: Prioritize Media */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.prioritize_media ?? false}
              onChange={(e) => onUpdate({ prioritize_media: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show reviews with photos/videos first</span>
          </label>
        </div>
      </div>
    </div>
  );
};
